import React from 'react'
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import { usePrepareStage } from './hooks'
import { getStatusTableRows, getPrepareResultTableRows } from './formatUtils'
import { OpenJsonButton } from '../../utility/OpenJsonButton'
import { downloadJson } from 'shared/utils/fileExport'
import { SummaryTable } from '../../SummaryTable'

import type { StageProps } from './types'

export function PrepareStage({ treeId, versionId }: StageProps) {
  const {
    prepareStatus,
    prepareResult,
    copyTree,
    handleStartStage,
    handleCopyTreeChange,
    canStart,
  } = usePrepareStage(treeId, versionId)

  return (
    <Box border={1} borderColor="grey.300" borderRadius={1} p={2} mb={2}>
      <Typography variant="h6" gutterBottom sx={{ fontStyle: 'italic' }}>
        Prepare
      </Typography>
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleStartStage}
          disabled={!canStart}
          size="small"
        >
          Start
        </Button>
        <FormControlLabel
          control={
            <Checkbox
              checked={copyTree}
              onChange={(e) => handleCopyTreeChange(e.target.checked)}
              size="small"
              disabled={!canStart}
            />
          }
          label="Copy tree"
        />
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ fontWeight: 'medium' }}
        >
          {copyTree
            ? 'This will correct a copy of the tree'
            : 'This will correct the original tree'}
        </Typography>
      </Box>

      {prepareStatus && (
        <Box sx={{ mb: 1 }}>
          {!prepareStatus.completed && (
            <Box sx={{ mb: 1 }}>
              <CircularProgress size={20} color="primary" />
            </Box>
          )}
          <SummaryTable
            rows={getStatusTableRows(prepareStatus, null)}
            firstColumnSx={{ fontStyle: 'italic' }}
          />
        </Box>
      )}

      {prepareResult && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <OpenJsonButton
              data={prepareResult}
              disabled={!prepareResult}
              buttonText="See JSON results"
            />
            <Button
              variant="text"
              size="small"
              onClick={() => {
                downloadJson(
                  `prepare_results_${treeId}_${versionId}.json`,
                  prepareResult,
                )
              }}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              Download JSON
            </Button>
          </Box>
          <SummaryTable
            rows={getPrepareResultTableRows(prepareResult)}
            firstColumnSx={{ fontStyle: 'italic' }}
          />
        </>
      )}
    </Box>
  )
}
